import { TreeNode, TreeIndexMap, LogLocation } from '../types';

export function normalizeEntries(entries: Array<{
  id?: string;
  fileID?: string;
  name: string;
  type: 'file' | 'directory';
  path?: string;
  children?: unknown[];
  files?: unknown[];
  directories?: unknown[];
}>, parentPath = ''): TreeNode[] {
  return entries.map((entry) => {
    const name = entry.name;
    const path = entry.path || (parentPath ? `${parentPath}/${name}` : name);
    const id = entry.id || entry.fileID || path;
    const type = entry.type === 'directory' ? 'folder' : 'file';
    const childrenSource = (entry.children || entry.files || entry.directories || []) as typeof entries;
    return {
      id,
      name,
      path,
      type,
      children: childrenSource.length ? normalizeEntries(childrenSource, path) : undefined,
    };
  });
}

export function buildTreeIndex(nodes: TreeNode[]): TreeIndexMap {
  const byId: Record<string, TreeNode> = {};
  const byPath: Record<string, TreeNode> = {};
  const walk = (list: TreeNode[]) => {
    list.forEach((node) => {
      byId[node.id] = node;
      byPath[node.path] = node;
      if (node.children) walk(node.children);
    });
  };
  walk(nodes);
  return { byId, byPath };
}

export function findFirstFile(nodes: TreeNode[]): TreeNode | null {
  for (const node of nodes) {
    if (node.type === 'file') return node;
    if (node.children) {
      const found = findFirstFile(node.children);
      if (found) return found;
    }
  }
  return null;
}

export function findParentId(nodes: TreeNode[], targetId: string, parentId: string | null = null): string | null {
  for (const node of nodes) {
    if (node.id === targetId) return parentId;
    if (node.children) {
      const found = findParentId(node.children, targetId, node.id);
      if (found) return found;
    }
  }
  return null;
}

export function removeNode(nodes: TreeNode[], targetId: string): { tree: TreeNode[]; removedIds: string[] } {
  let removed: string[] = [];
  const next = nodes
    .map((node) => {
      if (node.id === targetId) {
        removed.push(node.id);
        return null;
      }
      if (node.children) {
        const res = removeNode(node.children, targetId);
        removed = removed.concat(res.removedIds);
        return { ...node, children: res.tree };
      }
      return node;
    })
    .filter(Boolean) as TreeNode[];
  return { tree: next, removedIds: removed };
}

export function renameNode(nodes: TreeNode[], targetId: string, newName: string): TreeNode[] {
  return nodes.map((node) => {
    if (node.id === targetId) {
      const newPath = node.path ? node.path.replace(/[^/]+$/, newName) : newName;
      return { ...node, name: newName, path: newPath };
    }
    if (node.children) {
      return { ...node, children: renameNode(node.children, targetId, newName) };
    }
    return node;
  });
}

export function addChild(nodes: TreeNode[], parentId: string | null, child: TreeNode): TreeNode[] {
  if (parentId === null) {
    return [...nodes, child];
  }
  return nodes.map((node) => {
    if (node.id === parentId) {
      const children = node.children ? [...node.children, child] : [child];
      return { ...node, children };
    }
    if (node.children) {
      return { ...node, children: addChild(node.children, parentId, child) };
    }
    return node;
  });
}

export function extractLocationFromText(text: string): LogLocation | null {
  if (!text) return null;
  const match = text.match(/([A-Za-z0-9_./-]+\.[A-Za-z0-9]+):(\d+)(?::(\d+))?/);
  if (!match) return null;
  const path = match[1].replace(/\\/g, '/');
  const line = parseInt(match[2], 10) || 1;
  const column = match[3] ? parseInt(match[3], 10) || 1 : 1;
  return { path, line, column };
}

export function normalizePath(path: string) {
  return path.replace(/\\/g, '/').replace(/^\.?\//, '');
}

/**
 * Finds a node by ID in the tree.
 */
export function findNodeById(nodes: TreeNode[], id: string): TreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Moves a node from its current location to a new parent folder.
 * Returns the updated tree and a map of old paths to new paths for file cache updates.
 */
export function moveNode(
  nodes: TreeNode[],
  sourceId: string,
  targetFolderId: string
): { tree: TreeNode[]; pathChanges: Record<string, string> } {
  // First, find the source node
  const sourceNode = findNodeById(nodes, sourceId);
  if (!sourceNode) {
    return { tree: nodes, pathChanges: {} };
  }

  // Find the target folder to get its path
  const targetFolder = findNodeById(nodes, targetFolderId);
  if (!targetFolder || targetFolder.type !== 'folder') {
    return { tree: nodes, pathChanges: {} };
  }

  // Prevent moving a folder into itself or its descendants
  const isDescendant = (parentPath: string, childPath: string): boolean => {
    return childPath.startsWith(parentPath + '/');
  };
  if (sourceNode.type === 'folder' && (targetFolder.id === sourceId || isDescendant(sourceNode.path, targetFolder.path))) {
    return { tree: nodes, pathChanges: {} };
  }

  // Calculate path changes
  const pathChanges: Record<string, string> = {};
  const oldBasePath = sourceNode.path;
  const newBasePath = `${targetFolder.path}/${sourceNode.name}`;

  // Collect all paths that will change (for files in moved folders)
  const collectPaths = (node: TreeNode, oldBase: string, newBase: string) => {
    const newPath = node.path.replace(oldBase, newBase);
    pathChanges[node.path] = newPath;
    if (node.children) {
      node.children.forEach(child => collectPaths(child, oldBase, newBase));
    }
  };
  collectPaths(sourceNode, oldBasePath, newBasePath);

  // Remove the source node from its current location
  const { tree: treeWithoutSource } = removeNode(nodes, sourceId);

  // Update paths in the source node recursively
  const updatePaths = (node: TreeNode, oldBase: string, newBase: string): TreeNode => {
    const newPath = node.path.replace(oldBase, newBase);
    const newId = newPath; // ID is the path
    return {
      ...node,
      id: newId,
      path: newPath,
      children: node.children?.map(child => updatePaths(child, oldBase, newBase)),
    };
  };
  const updatedSourceNode = updatePaths(sourceNode, oldBasePath, newBasePath);

  // Add the source node to the target folder
  const finalTree = addChild(treeWithoutSource, targetFolderId, updatedSourceNode);

  return { tree: finalTree, pathChanges };
}
